/**
 * ASSESSMENT REST client (F3-B) — server-authoritative endpoints that don't go
 * through sync: gradebook matrix, bulk grading, cohort close and
 * submission file upload. Uses the same auth transport as storage
 * (cookie-BFF on web; Bearer injected by Rust on Tauri).
 */

import { apiFetch, apiJson } from "@/lib/rest";

// ─── Types (mirror of use_cases::gradebook / cohort_close's Rust DTOs) ────────

export interface GradebookCategoryDTO {
  id: string;
  name: string;
  weight: number;
  drop_lowest: number;
  position: number;
}
export interface GradebookItemDTO {
  id: string;
  name: string;
  category_id: string | null;
  source_kind: string;
  source_ref: string | null;
  max_score: number;
  weight: number;
  position: number;
}
export interface GradebookCellDTO {
  item_id: string;
  score: number | null;
  fraction: number;
  excused: boolean;
}
export interface GradebookStudentDTO {
  user_id: string;
  name: string | null;
  email: string | null;
  cells: GradebookCellDTO[];
  final_raw: number;
  final_value: number;
  final_display: string;
  passing: boolean;
}
export interface GradebookMatrixDTO {
  cohort_id: string;
  scheme_key: string;
  categories: GradebookCategoryDTO[];
  items: GradebookItemDTO[];
  students: GradebookStudentDTO[];
}

export interface BulkGradeEntry {
  assignment_id: string;
  user_id: string;
  score?: number | null;
  feedback?: string | null;
  status?: string;
}

export interface CloseSummary {
  cohort_id: string;
  scheme_key: string;
  completed: number;
  certificates: number;
}

/** GET /api/cohorts/:id/gradebook — matriz calculada (manager). */
export function fetchGradebook(cohortId: string): Promise<GradebookMatrixDTO> {
  return apiJson<GradebookMatrixDTO>(`/api/cohorts/${encodeURIComponent(cohortId)}/gradebook`);
}

/** POST /api/cohorts/:id/grades/bulk — bulk grading of assignments. */
export function submitBulkGrades(
  cohortId: string,
  grades: BulkGradeEntry[],
): Promise<{ upserted: number }> {
  return apiJson(`/api/cohorts/${encodeURIComponent(cohortId)}/grades/bulk`, {
    method: "POST",
    body: JSON.stringify({ grades }),
  });
}

/** POST /api/cohorts/:id/close — closes the cohort (finals + certificates). */
export function closeCohort(cohortId: string): Promise<CloseSummary> {
  return apiJson(`/api/cohorts/${encodeURIComponent(cohortId)}/close`, { method: "POST" });
}

/** POST /api/assignments/:id/submissions/:userId/files — sube un archivo de entrega. */
export async function uploadSubmissionFile(
  assignmentId: string,
  userId: string,
  cohortId: string,
  file: File,
): Promise<{ key: string; url: string; files: string[] }> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiFetch(
    `/api/assignments/${encodeURIComponent(assignmentId)}/submissions/${encodeURIComponent(userId)}/files?cohort_id=${encodeURIComponent(cohortId)}`,
    { method: "POST", body: form },
  );
  if (!response.ok) {
    throw new Error(`Upload failed (${response.status})`);
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return (await response.json()) as { key: string; url: string; files: string[] };
}
